/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react"
import { Button, Col, Form, Modal, Row } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { downloadAppFormPDF, getApplication, actions } from "../../../data/applicationSlice"
import { camelCaseToWords, downloadFile, questionnaire } from "../../../constant/utils"
import {
    getAnnualConstructionPercentage,
    getContractingServices,
    getEstimatedPercentageOfCompanyTotalRevenues,
    getRetentionAmount
} from "../../../data/enumSlice"
import Loader from "../../../components/Loader"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { byPrefixAndName } from "@awesome.me/kit-de80aecb6a/icons"
const { clearApplicationData } = actions

const ApplicantDetailModal = ({ showModal, handleCloseModal, applicationId }) => {
    const dispatch = useDispatch()
    const [application, setApplication] = useState({})
    const [errorMessage, setErrorMessage] = useState("")
    const { services, annualConstructionPercentage, estimatedPercentageOfCompanyTotalRevenues, retentionAmounts } =
        useSelector((state) => state.enums)

    const {
        fetchStatus,
        application: globalApplicationState,
        underwriterReferralReasonQuestionMaps
    } = useSelector((state) => state.application)

    useEffect(() => {
        if (showModal && applicationId) {
            setErrorMessage("")
            dispatch(getApplication(applicationId)).then((response) => {
                let app = response.payload
                let application = {}
                let underwriterReferralReasons = []

                application.name = app.firstNamedInsuredCompany
                application.principalContact = app.firstNamedInsuredContact
                application.mailingAddress = app.firstNamedInsuredAddress
                application.city = app.firstNamedInsuredCity
                application.state = app.firstNamedInsuredState
                application.zip = app.firstNamedInsuredZipCode
                application.desiredEffectiveDateOfCoverage = app.dateStartedUtc
                application.retroactiveDate = app.retroactiveDate
                application.bindEffectiveDate = app.bindEffectiveDateUtc

                app?.underwriterReferralReasons?.forEach((item) => {
                    underwriterReferralReasons.push(item?.reason?.toLowerCase())
                })
                application.underwriterReferralReasons = underwriterReferralReasons
                application.limitsAndRetentions = app.limitsAndRetentions.map((item) => {
                    const retentionAmount = retentionAmounts?.find(
                        (retention) => retention.value === item.retention
                    )?.name
                    return {
                        eachLossLimit: item.eachLossLimit,
                        aggregateLimit: item.aggregateLimit,
                        retentionAmount: retentionAmount ? `$${retentionAmount}` : "N/A"
                    }
                })

                application.coverageRequested = app.contractorPollutionLiabilityCoverageOnly
                    ? "Contractor Pollution Liability Coverage Only"
                    : "Contractor Pollution Liability Coverage and Professional Liability Coverage"

                application.contractingRevenuesCurrentCalendarYear = app.contractingRevenuesCurrentCalendarYear
                application.contractingRevenuesPriorCalendarYear = app.contractingRevenuesPriorCalendarYear

                application.contractingServices = Object.keys(app)
                    .filter((key) => key.startsWith("contractingService") && app[key])
                    .map((key) => {
                        if (key.endsWith("Label")) {
                            return {
                                key: app[key],
                                percentage: app["contractingServiceOther"],
                                isOtherField: true
                            }
                        }
                        return {
                            key: key,
                            percentage: app[key]
                        }
                    })

                application.annualConstructionValues = Object.keys(app)
                    .filter((key) => key.startsWith("annualConstructionValue") && app[key])
                    .map((key) => {
                        if (key.endsWith("Label")) {
                            return {
                                key: app[key],
                                percentage: app["annualConstructionValueOther"],
                                isOtherField: true
                            }
                        }
                        return {
                            key: key,
                            percentage: app[key]
                        }
                    })

                application.estimatedPercentageOfCompanyTotalRevenues = Object.keys(app)
                    .filter((key) => key.startsWith("companyTotalRevenue") && app[key])
                    .map((key) => {
                        if (key.endsWith("Label")) {
                            return {
                                key: app[key],
                                percentage: app["companyTotalRevenueOther"],
                                isOtherField: true
                            }
                        }
                        return {
                            key: key,
                            percentage: app[key]
                        }
                    })

                application.additionalInsureds = app?.additionalNamedInsureds
                    .filter((item) => item.type === "AdditionalInsured" && item.name !== "")
                    .map((insured) => {
                        return {
                            name: insured?.name,
                            role: insured?.relationship
                        }
                    })

                application.additionalNamedInsureds = app?.additionalNamedInsureds
                    .filter((item) => item.type === "AdditionalNamedInsured" && item.name !== "")
                    .map((insured) => {
                        return {
                            name: insured?.name,
                            role: insured?.relationship
                        }
                    })

                application.operationsPerformedOutsideOfUs = app?.operationsPerformedOutsideOfUs

                application.hasPurchasedProfessionalLiabilityOrContractorPollutionLiabilityInsurance =
                    app?.hasPurchasedProfessionalLiabilityOrContractorPollutionLiabilityInsurance

                application.hasPurchasedProfessionalLiabilityOrContractorPollutionLiabilityInsuranceExplanation =
                    app?.hasPurchasedProfessionalLiabilityOrContractorPollutionLiabilityInsuranceExplanation
                application.hasClaimsOrLegalActionsAgainstInsureds = app?.hasClaimsOrLegalActionsAgainstInsureds
                application.hasClaimsOrLegalActionsAgainstInsuredsExplanation =
                    app?.hasClaimsOrLegalActionsAgainstInsuredsExplanation
                application.hasPollutionIncidents = app?.hasPollutionIncidents
                application.hasPollutionIncidentsExplanation = app?.hasPollutionIncidentsExplanation
                application.hasDamageOrInjuryToProperty = app?.hasDamageOrInjuryToProperty
                application.hasDamageOrInjuryToPropertyExplanation = app?.hasDamageOrInjuryToPropertyExplanation

                application.contractingServices = application?.contractingServices.filter(
                    (item) => !item.key.endsWith("Other")
                )
                application.annualConstructionValues = application?.annualConstructionValues.filter(
                    (item) => !item.key.endsWith("Other")
                )
                application.estimatedPercentageOfCompanyTotalRevenues =
                    application?.estimatedPercentageOfCompanyTotalRevenues.filter((item) => !item.key.endsWith("Other"))
                setApplication(application)
            })
        }
    }, [applicationId, showModal])

    // useEffect(() => {
    //     !underwriterReferralReasonQuestionMaps?.length && dispatch(getUnderwriterReferralReasonQuestionMaps())
    // }, [])

    const hideApplicantDetailModal = () => {
        if (Object.keys(globalApplicationState).length > 0) {
            dispatch(clearApplicationData())
            handleCloseModal()
        } else {
            handleCloseModal()
        }
    }

    useEffect(() => {
        !retentionAmounts?.length && dispatch(getRetentionAmount())
        dispatch(getContractingServices())
        dispatch(getAnnualConstructionPercentage())
        dispatch(getEstimatedPercentageOfCompanyTotalRevenues())
    }, [])

    const convertStringToNumber = (value) => {
        value = value?.replace("Five", "5")
        value = value?.replace("Ten", "10")
        value = value?.replace("Fifteen", "15")
        value = value?.replace("Twenty", "20")
        value = value?.replace("TwentyFive", "25")
        value = value?.replace("Thirty", "30")
        value = value?.replace("ThirtyFive", "35")
        value = value?.replace("Forty", "40")
        value = value?.replace("FortyFive", "45")
        value = value?.replace("Fifty", "50")
        value = value?.replace("Thousand", "000")
        return Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0
        }).format(value)
    }

    const convertToCurrency = (value) => {
        return Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0
        }).format(value)
    }
    const getServiceName = (key, data) => {
        if (!key.includes("contractingService")) {
            return data.key
        }
        return services?.find((service) => service.key === key)?.name
    }

    const getAnnualConstruction = (key, data) => {
        if (!key.includes("annualConstructionValue")) {
            return data.key
        }
        return annualConstructionPercentage?.find((annualConstruction) => annualConstruction.key === key)?.name
    }

    const getEstimatedPercentageOfCompany = (key, data) => {
        if (!key.includes("companyTotalRevenue")) {
            return data.key
        }
        return estimatedPercentageOfCompanyTotalRevenues?.find((estimatedPercentage) => estimatedPercentage.key === key)
            ?.name
    }

    const downloadApplicationForm = () => {
        dispatch(downloadAppFormPDF(applicationId)).then(({ payload }) => {
            if (payload?.succeeded && payload?.file) {
                downloadFile(payload)
            } else if (!payload?.succeeded && payload?.errorMessage) {
                setErrorMessage(payload?.errorMessage)
            }
        })
    }

    const hasCPLCoverageOnly = application.coverageRequested === "Contractor Pollution Liability Coverage Only"

    const getWarningStyles = (names) =>
        names.some((name) => application?.underwriterReferralReasons?.includes(name?.toLowerCase()))
            ? { backgroundColor: "#FDF19F" }
            : {}

    return (
        <Modal
            show={showModal}
            onHide={hideApplicantDetailModal}
            size="lg"
            centered>
            <Modal.Header closeButton>
                <Modal.Title>Application Information</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Loader show={fetchStatus === "loading"} />
                <div className={`row justify-content-end ${!errorMessage ? "mb-2" : ""}`}>
                    <div className="col-auto">
                        <Button
                            variant="light"
                            className="btn-sm"
                            onClick={() => downloadApplicationForm()}
                            title="Download the application form to SIGN">
                            <FontAwesomeIcon icon={byPrefixAndName.fal["download"]} /> Application Form
                        </Button>
                    </div>
                </div>
                {errorMessage && (
                    <div className="d-flex justify-content-end  mb-2">
                        <Form.Text className="text-danger text-right">{errorMessage}</Form.Text>
                    </div>
                )}
                <Row className="user-detail-modal mx-1">
                    <Col xs={8}>Proposed First Named Insured </Col>
                    <Col xs={4}>{application?.name} </Col>

                    <Col xs={8}>Primary Contact at First Named Insured</Col>
                    <Col xs={4}>{application?.principalContact} </Col>

                    <Col xs={8}>Mailing address of First Named Insured</Col>
                    <Col xs={4}>{application?.mailingAddress} </Col>

                    <Col xs={8}>City</Col>
                    <Col xs={4}>{application?.city} </Col>

                    <Col xs={8}>State</Col>
                    <Col xs={4}>{application?.state} </Col>

                    <Col xs={8}>Zip</Col>
                    <Col xs={4}>{application?.zip} </Col>

                    <Col xs={8}>Desired Effective Date of Coverage </Col>
                    <Col xs={4}>
                        {" "}
                        {application?.bindEffectiveDate
                            ? new Date(application?.bindEffectiveDate).toLocaleDateString()
                            : null}{" "}
                    </Col>
                    <Col
                        xs={8}
                        style={getWarningStyles([
                            "CoverageRequestedContractorPollutionLiabilityCoverageOnlyPollutionCoverage",
                            "CoverageRequestedContractorPollutionLiabilityCoverageOnlyMoldCoverage",
                            "CoverageRequestedContractorPollutionLiabilityAndProfessionalLiabilityCoveragePollutionCoverage",
                            "CoverageRequestedContractorPollutionLiabilityAndProfessionalLiabilityCoverageMoldCoverage"
                        ])}>
                        Coverage Requested
                    </Col>
                    <Col
                        xs={4}
                        style={getWarningStyles([
                            "CoverageRequestedContractorPollutionLiabilityCoverageOnlyPollutionCoverage",
                            "CoverageRequestedContractorPollutionLiabilityCoverageOnlyMoldCoverage",
                            "CoverageRequestedContractorPollutionLiabilityAndProfessionalLiabilityCoveragePollutionCoverage",
                            "CoverageRequestedContractorPollutionLiabilityAndProfessionalLiabilityCoverageMoldCoverage"
                        ])}>
                        {application.coverageRequested}{" "}
                    </Col>
                    <Col xs={8}>Retroactive Date</Col>
                    <Col xs={4}> {application.retroactiveDate} </Col>

                    <Col xs={12}>
                        <strong>1. Desired Limits of Liability and Retention Amount: </strong>
                    </Col>

                    {application?.limitsAndRetentions?.map((data, index) => (
                        <React.Fragment key={index}>
                            <Col xs={2}> {`Option ${index + 1}`} </Col>
                            <Col xs={5}>
                                {convertToCurrency(data.eachLossLimit)} / {convertToCurrency(data.aggregateLimit)}{" "}
                            </Col>
                            <Col xs={5}>{data.retentionAmount}</Col>
                        </React.Fragment>
                    ))}

                    <Col xs={12}>
                        <strong>2. Insured’s total annual contracting revenues </strong>
                    </Col>

                    {application?.contractingRevenuesPriorCalendarYear && (
                        <>
                            <Col
                                xs={8}
                                style={getWarningStyles(["RevenueInExcessOfTwentyFiveMillion"])}>
                                Prior calendar year{" "}
                            </Col>
                            <Col
                                xs={4}
                                style={getWarningStyles(["RevenueInExcessOfTwentyFiveMillion"])}>
                                {Intl.NumberFormat("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                    minimumFractionDigits: 2
                                }).format(application?.contractingRevenuesPriorCalendarYear)}
                            </Col>
                        </>
                    )}

                    {application?.contractingRevenuesCurrentCalendarYear && (
                        <>
                            <Col
                                xs={8}
                                style={getWarningStyles(["RevenueInExcessOfTwentyFiveMillion"])}>
                                Estimated for the current calendar year{" "}
                            </Col>
                            <Col
                                xs={4}
                                style={getWarningStyles(["RevenueInExcessOfTwentyFiveMillion"])}>
                                {Intl.NumberFormat("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                    minimumFractionDigits: 2
                                }).format(application?.contractingRevenuesCurrentCalendarYear)}
                            </Col>
                        </>
                    )}

                    <Col xs={12}>
                        <strong>3. Contracting Services </strong>
                    </Col>
                    {application?.contractingServices?.map((data, index) => {
                        if (data?.key !== "contractingServiceOther") {
                            return (
                                <React.Fragment key={index}>
                                    <Col
                                        xs={8}
                                        style={
                                            data?.isOtherField
                                                ? getWarningStyles(["ContractingServiceOther"])
                                                : getWarningStyles([data?.key])
                                        }>
                                        {getServiceName(data?.key, data)}{" "}
                                    </Col>
                                    <Col
                                        xs={4}
                                        style={
                                            data?.isOtherField
                                                ? getWarningStyles(["ContractingServiceOther"])
                                                : getWarningStyles([data?.key])
                                        }>
                                        {Intl.NumberFormat("en-US", {
                                            style: "percent",
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 0
                                        }).format(data?.percentage / 100)}
                                    </Col>
                                </React.Fragment>
                            )
                        }
                    })}

                    <Col xs={12}>
                        <strong>{hasCPLCoverageOnly ? "4. N/A" : "4. Annual construction values"}</strong>
                    </Col>
                    {!hasCPLCoverageOnly &&
                        application?.annualConstructionValues?.map((data, index) => (
                            <React.Fragment key={index}>
                                <Col
                                    xs={8}
                                    style={
                                        data?.isOtherField
                                            ? getWarningStyles(["AnnualConstructionValueOther"])
                                            : getWarningStyles([data?.key])
                                    }>
                                    {getAnnualConstruction(data?.key, data)}{" "}
                                </Col>
                                <Col
                                    xs={4}
                                    style={
                                        data?.isOtherField
                                            ? getWarningStyles(["AnnualConstructionValueOther"])
                                            : getWarningStyles([data?.key])
                                    }>
                                    {Intl.NumberFormat("en-US", {
                                        style: "percent",
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0
                                    }).format(data?.percentage / 100)}
                                </Col>
                            </React.Fragment>
                        ))}

                    <Col xs={12}>
                        <strong>5. Estimated percentage of Company’s total revenues</strong>
                    </Col>
                    {application?.estimatedPercentageOfCompanyTotalRevenues?.map((data, index) => (
                        <React.Fragment key={index}>
                            <Col
                                xs={8}
                                style={
                                    data?.isOtherField
                                        ? getWarningStyles(["CompanyTotalRevenueOther"])
                                        : getWarningStyles([data?.key])
                                }>
                                {getEstimatedPercentageOfCompany(data?.key, data)}{" "}
                            </Col>
                            <Col
                                xs={4}
                                style={
                                    data?.isOtherField
                                        ? getWarningStyles(["CompanyTotalRevenueOther"])
                                        : getWarningStyles([data?.key])
                                }>
                                {" "}
                                {Intl.NumberFormat("en-US", {
                                    style: "percent",
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0
                                }).format(data?.percentage / 100)}
                            </Col>
                        </React.Fragment>
                    ))}

                    <Col
                        xs={12}
                        style={getWarningStyles(["AdditionalInsured"])}>
                        <strong>6. Additional insureds</strong>
                    </Col>
                    {application?.additionalInsureds?.map((data, index) => (
                        <React.Fragment key={index}>
                            <Col xs={8}>{data.name} </Col>
                            <Col xs={4}>{camelCaseToWords(data.role)}</Col>
                        </React.Fragment>
                    ))}

                    <Col
                        xs={12}
                        style={getWarningStyles(["AdditionalNamedInsured"])}>
                        <strong>7. Additional named insureds</strong>
                    </Col>

                    {application?.additionalNamedInsureds?.map((data, index) => (
                        <React.Fragment key={index}>
                            <Col xs={8}>{data.name} </Col>
                            <Col xs={4}>{camelCaseToWords(data.role)}</Col>
                        </React.Fragment>
                    ))}

                    {questionnaire?.map((data, index) => (
                        <React.Fragment key={index}>
                            <Col
                                xs={8}
                                style={getWarningStyles([data?.name])}>
                                <strong>
                                    {index + 8} - {hasCPLCoverageOnly && index + 8 === 13 ? "N/A" : data?.question}
                                </strong>
                            </Col>
                            <Col
                                xs={4}
                                style={getWarningStyles([data?.name])}>
                                {hasCPLCoverageOnly && index + 8 === 13 ? "" : application?.[data?.name] ? "Yes" : "No"}
                            </Col>
                            {data.explanationRequired && application?.[data?.name] && (
                                <Col
                                    xs={12}
                                    style={getWarningStyles([`${data?.name}Explanation`])}>
                                    Explanation: {application?.[`${data?.name}Explanation`]}
                                </Col>
                            )}
                        </React.Fragment>
                    ))}
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    className="btn-sm"
                    variant="secondary"
                    onClick={hideApplicantDetailModal}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default ApplicantDetailModal
